import React, { useState, useEffect } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase/firebaseConfig';

export default function AuthGuard() {
  const [state, setState] = useState('loading'); // 'loading' | 'unauthed' | 'denied' | 'ok'

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState('unauthed');
        return;
      }
      // Check `admins/{uid}` document exists
      try {
        const adminSnap = await getDoc(doc(db, 'admins', user.uid));
        if (!adminSnap.exists()) {
          setState('denied');
          return;
        }
        // Ensure the `admin` custom claim is set so Storage rules (which can't
        // reliably read Firestore at eval time) accept admin uploads, then
        // refresh the ID token so the claim is live this session.
        try {
          await httpsCallable(functions, 'claimAdmin')();
          await user.getIdToken(true);
        } catch {
          /* non-fatal: Storage rules fall back to the Firestore check */
        }
        setState('ok');
      } catch {
        setState('denied');
      }
    });
    return unsubscribe;
  }, []);

  if (state === 'loading')  return <FullScreenSpinner />;
  if (state === 'unauthed') return <Navigate to="/login" replace />;
  if (state === 'denied')   return <AccessDenied />;
  return <Outlet />;
}

function FullScreenSpinner() {
  return (
    <div style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: '#09090B', gap: 24,
    }}>
      <img
        src="/logo.png"
        alt="Team Fronx"
        style={{
          width: 110,
          height: 110,
          objectFit: 'contain',
          filter: 'drop-shadow(0 6px 24px rgba(212,181,116,0.45))',
        }}
      />
      <div style={{
        color: '#D8C8A4',
        fontSize: 11,
        letterSpacing: 2,
        textTransform: 'uppercase',
        fontWeight: 700,
      }}>
        Team Fronx · Admin
      </div>
      <div className="tf-spinner" />
    </div>
  );
}

function AccessDenied() {
  return (
    <div style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', background: '#0D0D0D', gap: 12,
    }}>
      <span style={{ fontSize: 48 }}>🚫</span>
      <h2 style={{ color: '#E63946', margin: 0 }}>Access Denied</h2>
      <p style={{ color: '#9A9A9A', textAlign: 'center', maxWidth: 320 }}>
        Your Google account is not registered as a Team Fronx admin.
        Ask the super-admin to add your UID to the <code>admins</code> collection.
      </p>
      <button
        onClick={() => auth.signOut()}
        style={{
          marginTop: 8, padding: '10px 24px', background: '#E63946',
          color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
          fontWeight: 700, fontSize: 14,
        }}
      >
        Sign Out
      </button>
    </div>
  );
}
